import { Component, Input, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { NgSelectComponent } from '@ng-select/ng-select';
import { combineLatest, Subscription } from 'rxjs';
import { distinctUntilChanged, first, pairwise, startWith } from 'rxjs/operators';
import { LeaveService } from 'src/app/services/leave.service';
import { Leave } from '../../../../../app/classes/leave';
import { environment } from '../../../../../environments/environment';
import { LeaveUserDate } from '../../../../classes/leave-user-date';
import { User } from '../../../../classes/user.class';
import { AddressDialogComponent } from '../../../../pages/transport-planning/planning-detail-employees/address-dialog/address-dialog.component';
import { ConfirmDialogService } from '../../../../services/confirm-dialog-service/confirm-dialog.service';
import { EntityField, EntityId, EntityType, UserId } from '../../../../services/entities/entity-type.class';
import {
    EmployeePlanningFg,
    EntityTypeService,
    PheFormData,
    PlanningFormData,
} from '../../../../services/entity-type.service';
import { PlanningService } from '../../../../services/planning/planning.service';
import { UserService } from '../../../../services/user/user.service';
import { Utils } from '../../../../utils.class';

interface HiringSelectItem {
    id: null;
    name: string;
    companyName?: string;
    hiringId?: number;
    isHiring: true;
    function?: string;
}

type EmployeeSelectItem =
    | (User & {
          leaves?: LeaveUserDate[];
          companyName?: string;
          isHiring?: never;
      })
    | HiringSelectItem;

@Component({
    selector: 'app-trackline-employee-planning',
    standalone: false,
    templateUrl: './trackline-employee-planning.component.html',
    styleUrl: './trackline-employee-planning.component.scss',
})
export class TracklineEmployeePlanningComponent implements OnInit, OnDestroy, OnChanges {
    saving: boolean;
    employees: User[];
    employeeMap: Map<number, User>;
    filteredEmployees: EmployeeSelectItem[];
    sums: { function: string; amount: number }[] = [];
    fcEmployee = new FormControl();
    hiringStaffPheFields: PheFormData[] = [];
    hiringStaffTableHeader: EntityField[] = [];
    private hiringStaffEntityType: EntityType;

    addHiringTagFn = (term: string) => ({ id: null, name: term, isHiring: true as const });

    searchFn = (term: string, item: EmployeeSelectItem) => {
        const t = term.toLowerCase();
        return !!(
            item.name?.toLowerCase().includes(t) ||
            (item as User).function?.toLowerCase().includes(t) ||
            item.companyName?.toLowerCase().includes(t)
        );
    };

    @Input() formData: PlanningFormData;
    leaves: Leave[];
    employeeLeaves: Record<number, LeaveUserDate[]> = {};

    @Input() fixedDate: Date;
    filterFunction: string | null = null;
    @ViewChild('addEmployeeSelect') addEmployeeSelect: NgSelectComponent;

    private subscriptions = new Subscription();

    constructor(
        private dialog: MatDialog,
        private confirmDialog: ConfirmDialogService,
        private userService: UserService,
        private entityTypeService: EntityTypeService,
        private leaveService: LeaveService,
        private planningService: PlanningService
    ) {}

    ngOnInit(): void {
        this.hiringStaffEntityType = environment.entitytypes['hiringStaff'] as EntityType;
        if (this.hiringStaffEntityType) {
            this.hiringStaffTableHeader = this.entityTypeService.getTableHeaders(this.hiringStaffEntityType);
            this.refreshHiringStaffPheFields();
            this.subscriptions.add(
                this.formData.form.controls.planningHas.valueChanges.subscribe(() => {
                    this.refreshHiringStaffPheFields();
                })
            );
        }
        this.subscriptions.add(
            this.formData.form.controls.planningHas.valueChanges.subscribe(() => {
                this.filterEmployees();
            })
        );
        this.subscriptions.add(
            this.formData.form.controls.employeePlanning.valueChanges.subscribe(value => {
                value.forEach(v => {
                    this.setEmployeeLeaves(v.user_id);
                });

                this.calculateSums();
            })
        );

        this.subscriptions.add(
            this.userService.getMap(true).subscribe(userMap => {
                this.employeeMap = userMap;
            })
        );

        const planningForm = this.formData.form.controls.planning;
        const pheMainForm = this.formData.form.controls.mainPlanningHas.controls.phe;

        if (planningForm.get('location')) {
            this.subscriptions.add(
                planningForm
                    .get('location')
                    .valueChanges.pipe(distinctUntilChanged())
                    .subscribe(newLocation => {
                        const previousLocation = this.formData.form.controls.planning.value['location'];

                        this.formData.form.controls.employeePlanning.controls.forEach(fg => {
                            if (fg.controls.work_begin.value === previousLocation) {
                                fg.controls.work_begin.setValue(newLocation);
                            }
                            if (fg.controls.work_end.value === previousLocation) {
                                fg.controls.work_end.setValue(newLocation);
                            }
                        });
                    })
            );
        }

        this.subscriptions.add(
            pheMainForm
                .get('begindate')
                .valueChanges.pipe(startWith(pheMainForm.get('begindate').value), pairwise())
                .subscribe(([previousStartTime, newStartTime]) => {
                    const previousTime = Utils.getTimeOrNull(previousStartTime);
                    const dayShift = Utils.dayDiff(previousStartTime, newStartTime);

                    this.formData.form.controls.employeePlanning.controls.forEach(fg => {
                        const employeeTime = Utils.getTimeOrNull(fg.value.begindate);

                        if (employeeTime === null || employeeTime === Utils.getTimeOrNull(newStartTime)) {
                            return;
                        }

                        if (previousTime === employeeTime) {
                            fg.controls.begindate.setValue(new Date(newStartTime));
                        } else if (dayShift) {
                            // De medewerker heeft een eigen begintijd, die mag niet op de tijd van de
                            // hoofdplanning gezet worden. Hij moet wel hetzelfde aantal dagen mee
                            // verschuiven, anders blijft de medewerkerplanning op de oude datum achter.
                            fg.controls.begindate.setValue(Utils.addDays(fg.value.begindate, dayShift));
                        }
                    });
                })
        );

        this.subscriptions.add(
            pheMainForm
                .get('enddate')
                .valueChanges.pipe(startWith(pheMainForm.get('enddate').value), pairwise())
                .subscribe(([previousEndTime, newEndTime]) => {
                    const previousTime = Utils.getTimeOrNull(previousEndTime);

                    this.formData.form.controls.employeePlanning.controls.forEach(fg => {
                        if (
                            previousTime === Utils.getTimeOrNull(fg.value.enddate) &&
                            Utils.getTimeOrNull(fg.value.enddate) !== Utils.getTimeOrNull(newEndTime)
                        ) {
                            fg.controls.enddate.setValue(new Date(newEndTime));
                        }

                        this.setEmployeeLeaves(fg.value.user_id);
                    });
                })
        );

        this.subscriptions.add(
            this.userService
                .getList(false, false)
                .pipe(first())
                .subscribe(users => {
                    this.employees = users;
                    const nonExistingUsers = this.formData.orginal?.user_planning
                        .map(up => up.user)
                        .filter(u => !!u && !this.employees.find(e => e.id === u.id));
                    if (nonExistingUsers?.length) {
                        this.employees.push(...nonExistingUsers);
                    }

                    this.formData.orginal?.user_planning?.forEach(userPlanning => {
                        const fg = this.entityTypeService.createEmployeeFormGroup(
                            this.formData,
                            +userPlanning.user_id,
                            this.employees,
                            this.subscriptions,
                            {
                                id: userPlanning.id,
                                _moved_from_id: userPlanning?._movedFrom?.id,
                                planning_id: userPlanning.planning_id,
                                begindate: userPlanning.begindate,
                                enddate: userPlanning.enddate,
                                comment: userPlanning.comment,
                                updated_at: userPlanning.updated_at,
                                origin: userPlanning.origin,
                                destination: userPlanning.destination,
                                work_begin: userPlanning.work_begin,
                                work_end: userPlanning.work_end,
                            },
                            userPlanning._movedFrom
                        );
                        this.formData.form.controls.employeePlanning.push(fg);
                        fg.markAsPristine();
                    });

                    this.calculateSums();

                    this.disableorEnableForms();
                })
        );

        const beginDate = pheMainForm.get('begindate');
        const endDate = pheMainForm.get('enddate');

        this.subscriptions.add(
            combineLatest([
                beginDate?.valueChanges.pipe(startWith(beginDate.value)),
                endDate?.valueChanges.pipe(startWith(endDate.value)),
            ]).subscribe(([begindate, enddate]) => {
                if (!begindate || !enddate) {
                    return;
                }

                this.subscriptions.add(
                    this.leaveService.getFilteredList(new Date(begindate), new Date(enddate)).subscribe(leaves => {
                        this.leaves = leaves;

                        this.setEmployeesLeave();
                    })
                );
            })
        );
    }

    setEmployeesLeave() {
        this.formData.form.controls.employeePlanning.controls.forEach(fg => {
            this.setEmployeeLeaves(fg.value.user_id);
        });
    }

    setEmployeeLeaves(userId: number) {
        const pheMain = this.formData.form.controls.mainPlanningHas;
        const pheEmployee = this.formData.form.controls?.employeePlanning.controls.find(
            c => c.value.user_id === userId
        );

        const employeeBegin = pheEmployee
            ? pheEmployee.controls.begindate.value
            : pheMain.controls.phe.get('begindate')?.value;
        const employeeEnd = pheEmployee
            ? pheEmployee.controls.enddate.value
            : pheMain.controls.phe.get('begindate')?.value;

        return (this.employeeLeaves[userId] = this.leaveService.getLeavesForUserOnDate(
            this.leaves,
            userId,
            employeeBegin,
            employeeEnd,
            4
        ));
    }

    addDefaults() {
        if (!this.formData.mainEntityType) {
            return;
        }

        this.entityTypeService.applyDefaultEmployees(this.formData, this.formData.mainEntityType, this.subscriptions);
    }

    ngOnChanges() {
        this.disableorEnableForms();
    }

    selectAddress(control: FormGroup<EmployeePlanningFg>) {
        if (this.formData.form.controls.employeePlanning.disabled) {
            return;
        }

        this.dialog.open(AddressDialogComponent, {
            width: '700px',
            height: '530px',
            maxHeight: '600px',
            data: {
                control,
                user: this.employees.find(e => e.id === control.value.user_id),
                location: this.formData.form.value.planning.location,
            },
        });
    }

    filterEmployees() {
        if (!this.employees) {
            return;
        }

        const currentEmployees = [
            ...this.formData.form.controls.employeePlanning.value.map(e => e.user_id),
            ...this.formData.form.controls.planningHas.value.map(e => e.phe.driver_user_id).filter(id => !!id),
        ];

        const employeeItems: EmployeeSelectItem[] = this.employees
            .filter(e => currentEmployees.indexOf(e.id) === -1)
            .map(e => {
                return { ...e, leaves: this.employeeLeaves[e.id] ?? this.setEmployeeLeaves(e.id) };
            });

        const seenHiring = new Map<string, HiringSelectItem>();
        this.planningService
            .getCurrentPlannings()
            .flatMap(p => p.planning_has ?? [])
            .filter(phe => phe.entitytype_key === 'hiringStaff' && phe.name)
            .forEach(phe => {
                const key = `${phe.name}|${phe.hiring_id ?? ''}`;
                if (!seenHiring.has(key)) {
                    seenHiring.set(key, {
                        id: null,
                        name: phe.name,
                        companyName: phe.hiring?.name,
                        hiringId: phe.hiring_id,
                        isHiring: true,
                        function: phe.hiring?.name,
                    });
                }
            });

        this.filteredEmployees = [...employeeItems, ...Array.from(seenHiring.values())];
    }

    calculateSums() {
        setTimeout(() => {
            this.filterEmployees();
            this.sums = [];
            if (this.formData.orginal?.traffic_controllers > 0) {
                this.sums.push({
                    function: 'Verkeersregelaar',
                    amount: 0,
                });
            }
            this.formData.form.controls.employeePlanning.value.forEach(employee => {
                const sum = this.sums.find(p => p.function == employee.function);
                if (sum) {
                    sum.amount++;
                } else {
                    this.sums.push({
                        function: employee.function,
                        amount: 1,
                    });
                }
            });
            if (this.hiringStaffPheFields?.length) {
                const hiringCompanyMap = new Map<number, string>();
                this.planningService
                    .getCurrentPlannings()
                    .flatMap(p => p.planning_has ?? [])
                    .filter(phe => phe.hiring_id && phe.hiring?.name)
                    .forEach(phe => hiringCompanyMap.set(phe.hiring_id, phe.hiring.name));

                this.hiringStaffPheFields.forEach(pheField => {
                    const hiringId = pheField.form.controls.phe.get('hiring_id')?.value as number;
                    const companyName = hiringId
                        ? (pheField.orginal?.hiring?.name ?? hiringCompanyMap.get(hiringId))
                        : null;
                    const label = companyName ?? 'Inhuur';
                    const sum = this.sums.find(p => p.function === label);
                    if (sum) {
                        sum.amount++;
                    } else {
                        this.sums.push({ function: label, amount: 1 });
                    }
                });
            }
        });
    }

    addEmployee(userId: number) {
        const fg = this.entityTypeService.createEmployeeFormGroup(
            this.formData,
            userId,
            this.employees,
            this.subscriptions
        );

        this.setEmployeeLeaves(userId);

        this.fcEmployee.reset();

        this.formData.form.controls.employeePlanning.push(fg);

        this.filterFunction = null;

        this.disableorEnableForms();

        this.formData.form.markAsDirty();
        this.formData.form.updateValueAndValidity();
    }

    deleteAll() {
        this.confirmDialog
            .confirm({
                title: 'Verwijderen',
                text: `Weet u zeker dat u al het personeel wilt verwijderen van deze planning?`,
                confirm: 'Verwijderen',
                cancel: 'Behouden',
            })
            .then(
                () => {
                    this.formData.form.controls.employeePlanning.clear();
                    [...this.hiringStaffPheFields].forEach(pheField => {
                        this.entityTypeService.removePheFromForm(this.formData, pheField);
                    });
                    this.refreshHiringStaffPheFields();
                    this.formData.form.markAsDirty();
                    this.formData.form.updateValueAndValidity();
                },
                () => {}
            );
    }

    delete(formgroup: FormGroup<EmployeePlanningFg>) {
        this.deleteEmployeeFg(formgroup);
    }

    private deleteEmployeeFg(formgroup: FormGroup<EmployeePlanningFg>) {
        if (formgroup) {
            this.formData.form.controls.employeePlanning.controls.splice(
                this.formData.form.controls.employeePlanning.controls.indexOf(formgroup),
                1
            );
            this.formData.form.controls.employeePlanning.value.splice(
                this.formData.form.controls.employeePlanning.value.indexOf(formgroup.value),
                1
            );
            this.formData.form.controls.employeePlanning.updateValueAndValidity();
            this.formData.form.controls.planningHas.controls.forEach(fg => {
                if (fg.controls.phe.get('driver_user_id')) {
                    fg.controls.phe.get('driver_user_id').updateValueAndValidity();
                }
            });
            this.formData.form.markAsDirty();
            this.formData.form.updateValueAndValidity();
        }
    }

    disableorEnableForms() {
        if (this.formData.mainPhe.orginal) {
            const entityType = environment.entitytypes[this.formData.mainPhe.orginal.entitytype_key];
            if (!this.entityTypeService.userCanEditPlanning(entityType, this.formData.orginal)) {
                this.formData.form.controls.employeePlanning?.controls.forEach(form => form.disable());
                this.formData.form.controls.employeePlanning.disable();
            }
        }
    }

    private refreshHiringStaffPheFields() {
        this.hiringStaffPheFields = this.formData.pheFields.filter(
            pheField => pheField.form.controls.phe.get('entitytype_key')?.value === 'hiringStaff'
        );
    }

    onEmployeeChange(item: EmployeeSelectItem) {
        if (!item) {
            return;
        }
        if (item.isHiring) {
            this.addHiringPhe(item.name, item.hiringId);
        } else {
            this.addEmployee(item.id);
        }
    }

    addHiringPhe(name: string, hiringId?: number) {
        const phe = this.entityTypeService.addPheToForm(this.formData, this.hiringStaffEntityType);
        (phe.form.controls.phe.get('entity_id') as unknown as FormControl).setValue(EntityId.HiringEmployee);
        setTimeout(() => {
            (phe.form.controls.phe.get('name') as unknown as FormControl).setValue(name);
            if (hiringId) {
                (phe.form.controls.phe.get('hiring_id') as unknown as FormControl).setValue(hiringId);
            }
        });
        const fcPerformer = this.formData.form.controls.planning.get('performer');
        if (!fcPerformer.value) {
            this.formData.form.controls.planning.get('performer').setValue(UserId.HiringPerformer);
        }

        this.fcEmployee.reset();
        this.formData.form.markAsDirty();
        this.calculateSums();
    }

    removeHiringPhe(pheField: PheFormData) {
        this.entityTypeService.removePheFromForm(this.formData, pheField);
        this.refreshHiringStaffPheFields();
        this.calculateSums();
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }
}
